import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ChevronLeft, ChevronRight, Sparkles, Crown, Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";

// -----------------------------------------------------------------------
// KingdomHourSection — the 12-part "Prophetic Principle Behind Every
// Breakthrough" series with a featured-episode-of-the-day hero + episode grid.
// -----------------------------------------------------------------------
export default function KingdomHourSection() {
  const [bundle, setBundle] = useState(null);
  const [selected, setSelected] = useState(null);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(true);
  const railRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/tv/kingdom-hour")
      .then(({ data }) => {
        if (!alive) return;
        setBundle(data);
        setSelected(data?.featured || data?.episodes?.[0] || null);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const episodes = bundle?.episodes || [];
  const show = bundle?.show;

  const backendOrigin = useMemo(() => {
    const url = process.env.REACT_APP_BACKEND_URL || "";
    return url.replace(/\/$/, "");
  }, []);

  // Convert relative /api/tv/media/... URLs to fully qualified
  const resolveMediaUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    if (u.startsWith("/api/")) return backendOrigin + u;
    return u;
  };

  const ytIdFromUrl = (u) => {
    if (!u) return null;
    const m = u.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  };

  const scrollRail = (dir) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  if (loading) {
    return (
      <section className="border-t border-gold-500/15 bg-gradient-to-b from-ink-paper/40 to-ink py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 text-center text-white/50 text-sm">
          Loading the Kingdom Hour…
        </div>
      </section>
    );
  }

  if (!show) return null;

  const featuredVideo = selected?.video_url ? resolveMediaUrl(selected.video_url) : "";
  const featuredYT = ytIdFromUrl(featuredVideo);

  return (
    <section
      className="relative border-t border-gold-500/15 py-20 md:py-28 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(11,32,83,0.35) 0%, rgba(11,15,26,1) 55%, rgba(11,15,26,1) 100%)",
      }}
      data-testid="kingdom-hour-section"
    >
      {/* Ambient royal glow */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 left-1/4 w-[500px] h-[500px] rounded-full bg-royal-500/20 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-gold-500/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Section header */}
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-[1px] bg-gold-500/60" />
            <Crown size={16} className="text-gold-400" />
            <p className="text-[11px] uppercase tracking-[0.32em] text-gold-400 font-semibold">
              The Kingdom Hour · A 12-Part Series
            </p>
          </div>
          <h2
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-white max-w-3xl"
            data-testid="kingdom-hour-title"
          >
            <span className="text-gold-400">The Prophetic Principle</span>
            <br />
            Behind Every Breakthrough
          </h2>
          <p className="mt-4 text-white/70 text-sm sm:text-base max-w-2xl italic font-serif">
            {show.tagline || "Why Kings Still Need the Prophet's Voice"}
          </p>
          {show.description && (
            <p className="mt-6 text-white/60 text-sm sm:text-base max-w-3xl leading-relaxed">
              {show.description}
            </p>
          )}
        </Reveal>

        {/* Featured Episode Hero */}
        <div className="mt-12 grid gap-8 lg:grid-cols-12 items-start">
          {/* Player card */}
          <Reveal className="lg:col-span-7 xl:col-span-8">
            <div
              className="relative aspect-video overflow-hidden border border-gold-500/30 bg-black shadow-[0_50px_120px_-40px_rgba(11,32,83,0.9)]"
              data-testid="kingdom-hour-player"
            >
              {featuredYT ? (
                <iframe
                  key={featuredYT}
                  src={`https://www.youtube-nocookie.com/embed/${featuredYT}?autoplay=0&rel=0&modestbranding=1&playsinline=1`}
                  title={selected?.title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="kingdom-hour-iframe"
                />
              ) : featuredVideo ? (
                <video
                  key={selected?.id}
                  src={featuredVideo}
                  poster={selected?.thumbnail}
                  controls
                  muted={muted}
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="kingdom-hour-video"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
                  {selected?.thumbnail && (
                    <img
                      src={selected.thumbnail}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-30"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-royal-900 via-royal-900/85 to-royal-900/50" />
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3.2, repeat: Infinity }}
                    className="relative w-20 h-20 rounded-full border border-gold-500/40 bg-gold-500/10 flex items-center justify-center mb-6"
                  >
                    <Sparkles size={30} className="text-gold-400" strokeWidth={1.4} />
                  </motion.div>
                  <p className="relative text-[10px] uppercase tracking-[0.32em] text-gold-400 mb-2">
                    Episode {selected?.episode_number} · Coming Soon
                  </p>
                  <p className="relative text-white font-serif text-2xl">{selected?.title}</p>
                  <p className="relative text-white/60 text-xs mt-3 max-w-sm">
                    Upload the video for this episode from the admin panel to publish it here.
                  </p>
                </div>
              )}

              {/* Mute toggle overlay — only meaningful for our <video> tag */}
              {featuredVideo && !featuredYT && (
                <button
                  onClick={() => setMuted((m) => !m)}
                  aria-label={muted ? "Unmute" : "Mute"}
                  data-testid="kingdom-hour-mute"
                  className="absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center bg-black/70 hover:bg-black text-white/80 hover:text-gold-300 border border-white/20 transition-colors"
                >
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
              )}
            </div>
          </Reveal>

          {/* Meta card */}
          <Reveal className="lg:col-span-5 xl:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected?.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="border border-gold-500/25 bg-ink-paper/60 backdrop-blur-sm p-6 md:p-8"
                data-testid="kingdom-hour-featured-meta"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-gold-400 font-semibold">
                    <Sparkles size={11} /> Featured Today
                  </span>
                  <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-white/40">
                    Episode {String(selected?.episode_number || 0).padStart(2, "0")}
                    <span className="mx-1 text-gold-500/60">/</span>12
                  </span>
                </div>
                <h3
                  className="font-serif text-2xl md:text-3xl text-white leading-tight mb-4"
                  data-testid="kingdom-hour-featured-title"
                >
                  {selected?.title}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  {selected?.description}
                </p>
                {selected?.air_date && (
                  <p className="text-[10px] uppercase tracking-[0.22em] text-gold-400/80 mb-4">
                    Air date · {new Date(selected.air_date).toLocaleDateString(undefined, {
                      month: "long", day: "numeric", year: "numeric"
                    })}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {selected?.video_url && (
                    <a
                      href={featuredVideo}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="kingdom-hour-open"
                      className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.14em] text-[11px] px-5 py-3 transition-colors"
                    >
                      <Play size={13} /> Watch Full Episode
                    </a>
                  )}
                  <a
                    href="/tv/live"
                    data-testid="kingdom-hour-live"
                    className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 uppercase tracking-[0.14em] text-[11px] px-5 py-3 transition-colors"
                  >
                    Go to 24/7 Live
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </div>

        {/* Episode Rail */}
        <div className="mt-16">
          <Reveal className="flex items-end justify-between gap-4 mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-1">
                All Twelve Episodes
              </p>
              <p className="font-serif text-2xl text-white">
                One prophetic principle per day, cycling all series long
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => scrollRail(-1)}
                aria-label="Scroll left"
                data-testid="kingdom-hour-rail-prev"
                className="w-9 h-9 flex items-center justify-center border border-gold-500/30 text-gold-400 hover:bg-gold-500 hover:text-ink transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => scrollRail(1)}
                aria-label="Scroll right"
                data-testid="kingdom-hour-rail-next"
                className="w-9 h-9 flex items-center justify-center border border-gold-500/30 text-gold-400 hover:bg-gold-500 hover:text-ink transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </Reveal>

          <div
            ref={railRef}
            className="flex gap-4 overflow-x-auto pb-6 scroll-smooth snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gold-500/20"
            data-testid="kingdom-hour-rail"
          >
            {episodes.map((ep, i) => {
              const active = selected?.id === ep.id;
              const isFeaturedToday = bundle?.featured?.id === ep.id;
              return (
                <motion.button
                  key={ep.id}
                  onClick={() => setSelected(ep)}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  data-testid={`kingdom-hour-ep-${ep.episode_number}`}
                  className={`group snap-start shrink-0 w-[280px] md:w-[320px] text-left border transition-all duration-300 ${
                    active
                      ? "border-gold-400 bg-gold-500/5 shadow-[0_20px_50px_-20px_rgba(212,175,55,0.4)]"
                      : "border-gold-500/15 bg-ink-paper/30 hover:border-gold-500/50 hover:bg-ink-paper/60"
                  }`}
                >
                  <div className="relative aspect-video overflow-hidden border-b border-gold-500/15 bg-ink">
                    {ep.thumbnail ? (
                      <img
                        src={ep.thumbnail}
                        alt={ep.title}
                        loading="lazy"
                        className={`w-full h-full object-cover transition-all duration-500 ${
                          active ? "scale-105" : "opacity-75 group-hover:opacity-100 group-hover:scale-[1.03]"
                        }`}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-royal-800 to-royal-950" />
                    )}
                    {/* Blue overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-royal-950/95 via-royal-950/30 to-transparent" />
                    {/* Episode number badge */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-royal-900/90 border border-gold-500/40 font-serif text-[11px] text-gold-300">
                        {String(ep.episode_number).padStart(2, "0")}
                      </span>
                      {isFeaturedToday && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-gold-500 text-ink px-1.5 py-1 font-semibold">
                          <Sparkles size={9} /> Today
                        </span>
                      )}
                      {ep.video_url && !isFeaturedToday && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-royal-800/90 text-gold-300 px-1.5 py-1 border border-gold-500/30">
                          Ready
                        </span>
                      )}
                    </div>
                    {/* Play affordance */}
                    <div className={`absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                      active ? "bg-gold-500 text-ink" : "bg-black/60 text-gold-300 group-hover:bg-gold-500 group-hover:text-ink"
                    }`}>
                      <Play size={13} fill="currentColor" />
                    </div>
                  </div>
                  <div className="p-4">
                    <p className={`font-serif text-base leading-snug line-clamp-2 min-h-[3rem] transition-colors ${
                      active ? "text-gold-200" : "text-white group-hover:text-gold-200"
                    }`}>
                      {ep.title}
                    </p>
                    <p className="mt-2 text-white/50 text-xs line-clamp-2 leading-snug">
                      {ep.description}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
